import React, { useEffect } from 'react';

import { FlowContextProvider, FlowValues, StepWrapper, useFlowState, useMutation, when } from '@betterment/js-runtime';
import { getLegacySearchParam } from '@betterment/shared-components';
import { pick as _pick } from 'es-toolkit/compat';
import { Helmet } from 'react-helmet';
import { Route, Routes, useSearchParams } from 'react-router-dom';

import Confirm from './Confirm';
import ProspectRequestCreationError from './ProspectRequestCreationError';
import ProspectSecondaryAddress from './ProspectSecondaryAddress';
import ProspectSecondaryInfo from './ProspectSecondaryInfo';
import ProspectSecondaryPhone from './ProspectSecondaryPhone';
import SelectAccount from './SelectAccount';
import Success from './Success';
import UserLookup from './UserLookup';

import BlockedError from '../Checking/components/BlockedError';
import { useCustomer } from '../Checking/components/CustomerProvider';
import NotFound from '../Checking/components/NotFound';
import SudoRouteComponent from '../Checking/components/SudoRouteComponent';
import { useUser } from '../Checking/contexts/UserProvider';
import {
  AccountCreationRequestResponse,
  Address,
  AddressValidationParams,
  JointInvitationRequestParams,
  JointInvitationRequestResponse,
  ProspectJointInvitationRequestParams,
  ProspectJointInvitationRequestResponse,
  UserMatch,
} from '../Checking/data';
import { CloseButton, ModalContent, ModalOverlay } from '../Checking/modal-helpers';
import { isLocked } from '../Checking/models/card';
import { isManualReview, spendingAccountName } from '../Checking/models/customer';
import { locationAssign } from '../Shared/path-helpers';
import { urlBuilder } from '../Shared/urlBuilder';

type RequestFlowValues = {
  email: string;
  socialSecurityNumber: string;
  invitedUser: UserMatch | null;
  accountId: number | null;
  isNewAccount: boolean | null;
  requestId: string;
  defaultAccountName: string | null;
  firstName: string | undefined;
  middleName: string | undefined;
  lastName: string | undefined;
  dateOfBirth: string | undefined;
  phoneNumber: string | undefined;
  residentialAddress: Address | undefined;
  isProspectSecondary: boolean;
  eligibilityError?: {
    title: string;
    message: string;
  };
};

export type RequestValues = FlowValues<RequestFlowValues>;

const initialValues: RequestFlowValues = {
  email: '',
  socialSecurityNumber: '',
  invitedUser: null,
  accountId: null,
  isNewAccount: null,
  requestId: '',
  defaultAccountName: '',
  firstName: undefined,
  middleName: undefined,
  lastName: undefined,
  dateOfBirth: undefined,
  phoneNumber: undefined,
  residentialAddress: undefined,
  isProspectSecondary: false,
};

export interface AddressFormValues {
  residentialAddress: Address;
}

function CloseFlowButton() {
  const {
    flowState: { returnToPath },
  } = useFlowState();
  return <CloseButton closeHandler={() => locationAssign(returnToPath)} />;
}

const AuthorizedController: React.FC = () => {
  const [jointInvitationRequest, { data: searchResult }] = useMutation<
    JointInvitationRequestResponse,
    JointInvitationRequestParams
  >(urlBuilder.apiV2CheckingJointInvitationRequest.absolute());

  const [issueAccountCreationRequest, { data: creationRequest }] = useMutation<AccountCreationRequestResponse, {}>(
    urlBuilder.apiV2CheckingAccountCreationRequests.absolute()
  );

  const [validateAddress] = useMutation<{}, AddressValidationParams>(urlBuilder.postValidateAddress());

  const [createProspectJointInvitationRequest] = useMutation<
    ProspectJointInvitationRequestResponse,
    ProspectJointInvitationRequestParams
  >(urlBuilder.createProspectSecondaryJointInvitationRequest());

  const [searchParams] = useSearchParams();
  const accountId = getLegacySearchParam(searchParams, 'account_id');
  const createNewAccount = getLegacySearchParam(searchParams, 'create_new_account');
  const { customer } = useCustomer();
  const { user } = useUser();

  const isCardLocked = isLocked(customer.card);
  const isCustomerLocked = customer.isLocked;
  const isCustomerInManualReview = isManualReview(customer);
  const isAccountGroupLocked = user?.checkingEligibilityStatus === 'account_group_locked';
  const isUserInManualReview = user?.checkingEligibilityStatus === 'manual_review';
  const isSuspended =
    user?.checkingEligibilityStatus === 'suspended' ||
    user?.checkingEligibilityStatus === 'suspended_documents_required';
  const isInBlockedState =
    isCardLocked ||
    isCustomerLocked ||
    isCustomerInManualReview ||
    isAccountGroupLocked ||
    isSuspended ||
    isUserInManualReview;
  const residentialAddress = _pick(user?.residentialAddress, ['addressLine1', 'addressLine2', 'city', 'state', 'zip']);

  useEffect(() => {
    if (!isInBlockedState) {
      void issueAccountCreationRequest();
    }
  }, [isInBlockedState, issueAccountCreationRequest]);

  const account = customer.accounts.find(({ id }) => id === parseInt(accountId || '', 10));

  if (accountId && !account) {
    return <NotFound />;
  }

  let defaultStep;
  if (isInBlockedState) {
    defaultStep = 'locked_error';
  } else if (accountId || createNewAccount) {
    defaultStep = 'user_lookup';
  }

  return (
    <ModalOverlay>
      <Helmet>
        <title>Joint Account Invitation Request</title>
      </Helmet>
      <ModalContent aria-label="joint account invitation request">
        <FlowContextProvider
          defaultStep={defaultStep}
          initialValues={{ ...initialValues, accountId, ...creationRequest, residentialAddress }}
          basePath="/checking/joint_accounts"
          routePath=""
          returnToPath={urlBuilder.getAppChecking.absolute()}
        >
          <CloseFlowButton />
          <Routes>
            <Route
              path="/select_account"
              element={
                <StepWrapper<RequestValues> name="select_account">
                  {stepProps => <SelectAccount customer={customer} {...stepProps} />}
                </StepWrapper>
              }
            />
            <Route
              path="/user_lookup"
              element={
                <StepWrapper<RequestValues>
                  name="user_lookup"
                  next={[['prospect_secondary_info', when((_, { values }) => values.isProspectSecondary)], 'confirm']}
                >
                  {stepProps => (
                    <UserLookup
                      searchResult={searchResult}
                      jointInvitationRequest={jointInvitationRequest}
                      {...stepProps}
                    />
                  )}
                </StepWrapper>
              }
            />
            <Route
              path="/confirm"
              element={
                <StepWrapper<RequestValues> name="confirm">{stepProps => <Confirm {...stepProps} />}</StepWrapper>
              }
            />
            <Route
              path="/success"
              element={
                <StepWrapper<RequestValues> name="success">
                  {stepProps => <Success {...stepProps} spendingAccountName={spendingAccountName(customer)!} />}
                </StepWrapper>
              }
            />
            <Route
              path="/prospect_secondary_info"
              element={
                <StepWrapper<RequestValues> name="prospect_secondary_info">
                  {stepProps => <ProspectSecondaryInfo {...stepProps} />}
                </StepWrapper>
              }
            />
            <Route
              path="/prospect_secondary_phone"
              element={
                <StepWrapper<RequestValues> name="prospect_secondary_phone">
                  {stepProps => <ProspectSecondaryPhone {...stepProps} />}
                </StepWrapper>
              }
            />
            <Route
              path="/prospect_secondary_address"
              element={
                <StepWrapper<RequestValues>
                  name="prospect_secondary_address"
                  next={[['confirm', when((_, { values }) => !!values.requestId)], 'prospect_request_creation_error']}
                >
                  {stepProps => (
                    <ProspectSecondaryAddress
                      {...stepProps}
                      validateAddress={validateAddress}
                      createProspectJointInvitationRequest={createProspectJointInvitationRequest}
                    />
                  )}
                </StepWrapper>
              }
            />
            <Route
              path="/prospect_request_creation_error"
              element={
                <StepWrapper<RequestValues> name="prospect_request_creation_error">
                  {stepProps => <ProspectRequestCreationError {...stepProps} />}
                </StepWrapper>
              }
            />
            <Route
              path="/locked_error"
              element={
                <StepWrapper name="locked_error">
                  <BlockedError
                    title="Joint Checking signup"
                    customer={customer}
                    accountId={customer.card.spendingAccountId}
                    user={user!}
                  />
                </StepWrapper>
              }
            />
          </Routes>
        </FlowContextProvider>
      </ModalContent>
    </ModalOverlay>
  );
};

const JointInvitationRequestsController: React.FC = () => {
  return (
    <SudoRouteComponent>
      <AuthorizedController />
    </SudoRouteComponent>
  );
};

export default JointInvitationRequestsController;
